<template>
    <TransitionRoot
        as="template"
        :show="open"
        @after-leave="emit('afterLeave')"
    >
        <Dialog
            as="div"
            class="fixed inset-0 z-20 overflow-y-auto"
            :open="open"
            @close="emit('close')"
        >
            <div class="flex min-h-full justify-center p-4 text-center">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay
                        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    />
                </TransitionChild>

                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <!-- Centred by an automatic margin rather than by the flex
                         container: a panel taller than the screen and centred by
                         `items-center` overflows it equally in both directions,
                         and whatever it overflows above the scroll container's
                         start edge can never be scrolled back to. An automatic
                         margin centres the same way, collapses to zero on the
                         side with no room left, and keeps the flex line from
                         stretching the panel to the container's full height. -->
                    <div
                        ref="panel"
                        data-test="dialog-panel"
                        class="m-auto"
                        :class="panelClass"
                    >
                        <slot />
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";

defineProps<{
    open: boolean;
    /** Classes for the panel itself; a vertical margin here defeats its automatic centring. */
    panelClass: string;
}>();

const emit = defineEmits<{
    /** The customer asked to leave — the Escape key, or a click on the overlay. */
    (event: "close"): void;
    /** The closing transition has finished, so whatever the panel held can be torn down. */
    (event: "afterLeave"): void;
}>();

const panel = ref<HTMLElement | null>(null);

/**
 * The panel element, for a caller sizing its own contents to the room left on screen.
 *
 * Exposed rather than handed to the slot: its consumer is a composable in the caller's script, and
 * a slot prop is only in scope inside the template.
 */
defineExpose({ panel });
</script>
